import { toast } from 'sonner';
import Avatar from '../Avatar';
import MessageBubble from '../MessageBubble';
import { IntentFeedbackButton } from './IntentFeedbackButton';
import ChatTypingIndicator from './ChatTypingIndicator';
import { useLanguage } from '../../context/LanguageContext';

/**
 * ChatMessages — extraído de ChatPanel.jsx en P3.1 sesión 3/4 (feb-2026).
 *
 * Renderiza el área de mensajes del chat: drop zone para imágenes,
 * overlay de drag, preview de imagen arrastrada, lista de mensajes con
 * pills colapsables + bubble expandido, y typing indicator.
 *
 * Reglas del cuadro negro (CRÍTICO):
 * - NO maneja streaming. NO maneja fetch. NO maneja AbortController.
 * - NO maneja scrollToBottom (el padre conserva `messagesEndRef` y lo
 *   pasa como prop para que el ChatMessages lo monte al final).
 * - Solo UI + callbacks (onUpdateMessages, onGoToWorkspace, drag handlers).
 * - JSX y clases Tailwind IDÉNTICOS al original.
 */
const ChatMessages = ({
  // refs (vienen del padre para mantener scroll inteligente)
  dropZoneRef,
  messagesEndRef,
  // estado
  messages,
  conversationId,
  isLoading,
  awaitingResponseRef,
  // drag & drop
  isDragging,
  draggedImage,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  // 🟥 CUADRO NEGRO rev6 — Modelo nuevo: últimos 3 abiertos + historial
  // oculto pero accesible. Los props `expandedIdx`/`setExpandedIdx` quedan
  // por compatibilidad pero NO se usan; el padre pasa los helpers nuevos.
  // eslint-disable-next-line no-unused-vars
  expandedIdx,
  // eslint-disable-next-line no-unused-vars
  setExpandedIdx,
  isMessageExpanded,
  toggleMessageExpansion,
  detectMessageIcon,
  getIconTooltip,
  // i18n
  t,
  // callbacks
  onUpdateMessages,
  onGoToWorkspace,
  // P22 · Intent A/B — cuando el usuario elige un botón inline, este callback
  // reenvía el mensaje original al backend con `force_category` = "A" | "B".
  onIntentDecision,
}) => {
  const { language } = useLanguage();
  const total = messages.length;

  return (
    <div
      ref={dropZoneRef}
      className={`flex-1 overflow-y-auto p-4 pr-2 mr-1 space-y-6 scrollbar-thin relative transition-all ${
        isDragging ? 'ring-2 ring-cyan-500 ring-inset bg-cyan-500/5' : ''
      }`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Overlay de Drag & Drop */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-zinc-900/80 backdrop-blur-sm pointer-events-none">
          <div className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-dashed border-cyan-500 bg-cyan-500/10">
            <svg className="w-12 h-12 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-lg font-medium text-cyan-300">{t.dropImageHere}</p>
            <p className="text-sm text-zinc-400">{t.willAppearInChat}</p>
          </div>
        </div>
      )}

      {/* Preview de imagen siendo arrastrada */}
      {draggedImage && !isDragging && (
        <div className="flex justify-end mb-4">
          <div className="bg-zinc-800/60 rounded-2xl rounded-tr-sm px-3 py-2 max-w-[80%] border border-zinc-700/30">
            <div className="flex items-center gap-2">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-700">
                <img src={draggedImage.base64} alt="Preview" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{draggedImage.filename}</p>
                <p className="text-xs text-zinc-500">{t.processing}</p>
              </div>
              <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        </div>
      )}

      {messages.length === 0 && !draggedImage ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <Avatar size="lg" />
          <h3 className="mt-4 text-lg font-medium text-zinc-300 light:text-zinc-700">
            {t.damyxiaIsYourOption}
          </h3>
          <p className="mt-2 text-sm text-zinc-500">
            {language === 'es'
              ? 'Arrastra una imagen aquí o escribe un mensaje'
              : 'Drag an image here or type a message'}
          </p>
        </div>
      ) : (
        <>
          {messages.map((msg, idx) => {
            // 🟥 CUADRO NEGRO rev6 — Cada mensaje decide su estado:
            //   - últimos 3 → abiertos por defecto
            //   - mensajes abiertos manualmente → abiertos (sobreviven)
            //   - resto → cerrados
            // El usuario puede invertir individualmente con "Ver" / "Cerrar".
            const isExpanded = isMessageExpanded(idx, total);
            const isLast = idx === total - 1;

            if (!isExpanded) {
              // Pill compacto colapsado.
              const isUser = msg.role === 'user';
              const label = isUser ? (language === 'es' ? 'Yo' : 'Me') : 'DamyxIA';
              // FASE 13.1: icono según el tipo de contenido detectado.
              const contentIcon = detectMessageIcon(msg.content || '');
              // FASE 13.3: tooltip descriptivo si hay icono; fallback al
              // preview del contenido (primeros 80 chars).
              const iconTooltip = getIconTooltip(contentIcon);
              const pillTitle = iconTooltip || (msg.content || '').slice(0, 80);
              return (
                <div
                  key={`${idx}-collapsed`}
                  className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} animate-in fade-in duration-150`}
                  data-testid={`chat-msg-collapsed-${idx}`}
                >
                  <button
                    onClick={() => toggleMessageExpansion(idx, total)}
                    className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      isUser
                        ? 'bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30'
                        : 'bg-zinc-800/60 hover:bg-zinc-700/70 text-zinc-300 border border-zinc-700/40'
                    } ${msg.reverted ? 'opacity-50 line-through' : ''}`}
                    data-testid={`chat-msg-collapsed-btn-${idx}`}
                    data-content-icon={contentIcon || ''}
                    data-icon-tooltip={iconTooltip || ''}
                    title={pillTitle}
                  >
                    <span>{label}</span>
                    {contentIcon && (
                      <span className="text-[11px] leading-none" aria-hidden="true">
                        {contentIcon}
                      </span>
                    )}
                    <span className="text-[10px] opacity-60 group-hover:opacity-100 transition-opacity">▸</span>
                  </button>
                  {/* 🟦 V9.5 — Toggle Ver/Cerrar text under the pill. */}
                  <span
                    onClick={() => toggleMessageExpansion(idx, total)}
                    className="mt-1 text-[12px] text-zinc-500 hover:text-zinc-300 cursor-pointer select-none transition-colors"
                    data-testid={`chat-msg-toggle-ver-${idx}`}
                  >
                    {language === 'es' ? 'Ver' : 'View'}
                  </span>
                </div>
              );
            }

            return (
              <div
                key={idx}
                className="animate-in fade-in slide-in-from-top-1 duration-150"
                data-testid={`chat-msg-expanded-${idx}`}
              >
                {/* Doble-click en el bubble → colapsa este mensaje
                    (excepto el último, que siempre permanece visible). */}
                <div
                  onDoubleClick={() => {
                    if (!isLast) toggleMessageExpansion(idx, total);
                  }}
                >
                  {/* P22 · Badge "solo chat" + P23.1 botón feedback "¿acerté?".
                      Se OCULTA cuando el toggle está OFF (redundante:
                      el toggle en el header ya lo dice). Solo se muestra
                      cuando toggle=ON o toggle=null (sin proyecto activo). */}
                  {msg.role === 'assistant' &&
                    msg.intent_category === 'A' &&
                    !msg.intent_prompt &&
                    msg.toggle_state !== 'OFF' && (
                      <div className="mb-1 ml-11 flex items-center gap-1.5 flex-wrap">
                        <div
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-zinc-800/60 text-zinc-400 text-[10px] font-medium border border-zinc-700/50"
                          title={
                            language === 'es'
                              ? 'DamyxIA respondió solo en el chat. No modificó el proyecto.'
                              : 'DamyxIA answered in chat only. The project was not modified.'
                          }
                          data-testid={`intent-chat-only-badge-${idx}`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          {language === 'es' ? 'Solo chat' : 'Chat only'}
                        </div>
                        <IntentFeedbackButton
                          predictedCategory="A"
                          originalMessage={
                            idx > 0 ? messages[idx - 1]?.content || '' : ''
                          }
                          language={language}
                        />
                      </div>
                    )}
                  <MessageBubble
                    message={msg}
                    conversationId={conversationId}
                    previousMessage={idx > 0 ? messages[idx - 1] : null}
                    isNewMessage={
                      awaitingResponseRef.current &&
                      msg.role === 'assistant' &&
                      idx === total - 1
                    }
                    onDeleteMessage={() => {
                      const newMessages = messages.filter((m, i) => i !== idx);
                      if (onUpdateMessages) {
                        onUpdateMessages(newMessages);
                        toast.success(t.messageDeleted);
                      }
                    }}
                    onExecuteCode={() => {
                      if (onGoToWorkspace) onGoToWorkspace();
                      toast.success(
                        language === 'es'
                          ? '¡Código listo en el área de trabajo!'
                          : 'Code ready in workspace!',
                      );
                    }}
                    onCollapseMessage={() => toggleMessageExpansion(idx, total)}
                    messageIdx={idx}
                  />
                  {/* P22 · Botones inline A/B — sólo en el último mensaje del
                      asistente y sólo si viene con intent_prompt (AMBIGUOUS). */}
                  {msg.role === 'assistant' &&
                    msg.intent_prompt &&
                    idx === total - 1 && (
                      <div
                        className="mt-2 ml-11 flex flex-wrap gap-2"
                        data-testid={`intent-ab-buttons-${idx}`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (onIntentDecision) {
                              onIntentDecision(
                                msg.intent_prompt.original_message || '',
                                'B',
                              );
                            }
                          }}
                          className="px-3 py-1.5 text-sm rounded-full bg-cyan-600 hover:bg-cyan-500 text-white transition-colors"
                          data-testid={`intent-ab-btn-apply-${idx}`}
                        >
                          {language === 'es'
                            ? 'Aplicar en el proyecto'
                            : 'Apply to the project'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (onIntentDecision) {
                              onIntentDecision(
                                msg.intent_prompt.original_message || '',
                                'A',
                              );
                            }
                          }}
                          className="px-3 py-1.5 text-sm rounded-full bg-zinc-700 hover:bg-zinc-600 text-zinc-100 transition-colors"
                          data-testid={`intent-ab-btn-chat-${idx}`}
                        >
                          {language === 'es'
                            ? 'Solo mostrar el código aquí'
                            : 'Just show the code here'}
                        </button>
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Typing indicator (3-dot loader) */}
      <ChatTypingIndicator isStreaming={isLoading} />

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
